// CRM Snippets — rehouses the classic Snippets screen into the Deliverability shell.
// Snippets are reusable reply blocks (canned responses) inserted into replies via the
// ReplyEditor "Snippets" button. This screen is the CRUD manager: list (search, usage)
// + editor (title, optional shortcut, rich body). Self-contained, client-scoped.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import ReplyEditor from '../../components/ReplyEditor';

const blankDraft = { id: null, shortcut: '', title: '', body_html: '' };

export default function CrmSnippets({ clientId }) {
  const { getToken } = useAuth();
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSnippets = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/snippets`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setSnippets(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId, getToken]);

  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  const openNew = () => setDraft({ ...blankDraft });
  const openEdit = (s) => setDraft({ id: s.id, shortcut: s.shortcut || '', title: s.title || '', body_html: s.body_html || s.body_text || '' });
  const closeEditor = () => setDraft(null);

  const save = useCallback(async () => {
    if (!draft) return;
    if (!draft.title.trim() || !(draft.body_html || '').trim()) { toast.error('Title and body are both required'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const cleanHtml = (draft.body_html === '<p></p>') ? '' : draft.body_html;
      const tmp = document.createElement('div');
      tmp.innerHTML = cleanHtml;
      const bodyText = tmp.textContent || '';
      const payload = { shortcut: draft.shortcut.trim() || null, title: draft.title.trim(), body_html: cleanHtml, body_text: bodyText };
      const res = draft.id
        ? await fetch(`${API_BASE}/snippets/${draft.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) })
        : await fetch(`${API_BASE}/clients/${clientId}/snippets`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Save failed (${res.status})`); }
      toast.success(draft.id ? 'Snippet updated' : 'Snippet created');
      setDraft(null);
      await fetchSnippets();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }, [draft, clientId, getToken, fetchSnippets]);

  const remove = async (s) => {
    if (!window.confirm(`Delete snippet "${s.title}"?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/snippets/${s.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success('Snippet deleted');
      if (draft && draft.id === s.id) setDraft(null);
      await fetchSnippets();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = snippets.filter(s => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (s.title || '').toLowerCase().includes(q) || (s.shortcut || '').toLowerCase().includes(q) || (s.body_text || '').toLowerCase().includes(q);
  });

  if (draft) {
    return (
      <div className="crmsn-root">
        <div className="crmsn-editor">
          <div className="crmsn-editor-head">
            <h2 className="crmsn-h2">{draft.id ? 'Edit snippet' : 'New snippet'}</h2>
            <button className="crmsn-btn" onClick={closeEditor} disabled={saving}>Back to list</button>
          </div>
          <div className="crmsn-card">
            <div className="crmsn-grid">
              <div className="crmsn-f">
                <label className="crmsn-lbl">Title</label>
                <input className="crmsn-input" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Send calendar link" autoFocus />
              </div>
              <div className="crmsn-f">
                <label className="crmsn-lbl">Shortcut (optional)</label>
                <input className="crmsn-input" value={draft.shortcut} onChange={e => setDraft({ ...draft, shortcut: e.target.value })} placeholder="e.g. /calendar" />
              </div>
            </div>
            <div className="crmsn-f">
              <label className="crmsn-lbl">Body</label>
              <ReplyEditor value={draft.body_html} onChange={html => setDraft({ ...draft, body_html: html })} clientId={clientId} placeholder="The reusable text you'll drop into replies." />
            </div>
            <div className="crmsn-editor-actions">
              <button className="crmsn-btn" onClick={closeEditor} disabled={saving}>Cancel</button>
              <button className="crmsn-btn crmsn-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (draft.id ? 'Save snippet' : 'Create snippet')}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="crmsn-root">
      <div className="crmsn-head">
        <div className="crmsn-search-wrap">
          <input className="crmsn-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search snippets…" />
        </div>
        <button className="crmsn-btn crmsn-btn--primary" onClick={openNew}>+ New snippet</button>
      </div>

      {error && <div className="crmsn-error">{error}</div>}

      {loading ? (
        <div className="crmsn-empty">Loading snippets…</div>
      ) : filtered.length === 0 ? (
        <div className="crmsn-empty">
          {snippets.length === 0 ? 'No snippets yet. Create reusable blocks to drop into replies from the composer.' : 'No snippets match your search.'}
        </div>
      ) : (
        <div className="crmsn-list">
          {filtered.map(s => (
            <div className="crmsn-row" key={s.id} onClick={() => openEdit(s)}>
              <div className="crmsn-row-main">
                <div className="crmsn-row-top">
                  <span className="crmsn-row-title">{s.title}</span>
                  {s.shortcut && <span className="crmsn-row-shortcut">{s.shortcut}</span>}
                </div>
                <div className="crmsn-row-preview">{s.body_text || ''}</div>
              </div>
              <div className="crmsn-row-side">
                <span className="crmsn-row-uses">{s.usage_count || 0} use{(s.usage_count || 0) === 1 ? '' : 's'}</span>
                <button className="crmsn-row-del" onClick={e => { e.stopPropagation(); remove(s); }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
